import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Select from 'react-select';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';

const localizer = momentLocalizer(moment);

const TaskCalendar = () => {
  const [tasks, setTasks] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'tasks'));
        const tasksData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          start: doc.data().startDate.toDate(),
          end: doc.data().deadline.toDate(),
        }));
        setTasks(tasksData);
        setFilteredTasks(tasksData);
      } catch (error) {
        logger.error('Error fetching tasks:', error);
      }
    };

    const fetchMechanics = async () => {
      try {
        const q = query(collection(db, 'employees'), where('role', '==', 'Mechanic'));
        const querySnapshot = await getDocs(q);
        setMechanics(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        logger.error('Error fetching mechanics:', error);
      }
    };

    const fetchJobCards = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'jobs'));
        setJobCards(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        logger.error('Error fetching job cards:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
    fetchMechanics();
    fetchJobCards();
  }, []);

  const handleMechanicChange = (selectedOption) => {
    setSelectedMechanic(selectedOption);
  };

  const handleJobCardChange = (selectedOption) => {
    setSelectedJobCard(selectedOption);
  };

  useEffect(() => {
    let filtered = tasks;
    if (selectedMechanic) {
      filtered = filtered.filter(task => task.assignedTo === selectedMechanic.value);
    }
    if (selectedJobCard) {
      filtered = filtered.filter(task => task.jobCardNo === selectedJobCard.value);
    }
    setFilteredTasks(filtered);
  }, [selectedMechanic, selectedJobCard, tasks]);

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3B82F6'; // blue for new
    if (event.status === 'completed') {
      backgroundColor = '#22C55E'; // green
    } else if (event.status === 'in-progress') {
      backgroundColor = '#F59E0B'; // amber
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        fontWeight: '600'
      }
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600 py-4 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <CalendarDaysIcon className="h-10 w-10 text-white mr-3" />
            <h1 className="text-3xl font-black text-white">Task Calendar</h1>
          </div>
          <p className="text-amber-50 text-sm">Visual schedule of mechanic tasks and deadlines</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Filter by Mechanic</label>
              <Select
                options={mechanics.map(mechanic => ({
                  value: mechanic.id,
                  label: `${mechanic.firstName} ${mechanic.lastName}`
                }))}
                onChange={handleMechanicChange}
                value={selectedMechanic}
                isClearable
                placeholder="All Mechanics"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    borderWidth: '2px',
                    borderColor: '#d1d5db',
                    '&:hover': { borderColor: '#f59e0b' },
                    '&:focus-within': { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.2)' }
                  })
                }}
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Filter by Job Card</label>
              <Select
                options={jobCards.map(job => ({
                  value: job.jobCardNo,
                  label: `${job.jobCardNo} - ${job.vehicleMake} ${job.vehicleModel}`
                }))}
                onChange={handleJobCardChange}
                value={selectedJobCard}
                isClearable
                placeholder="All Job Cards"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    borderWidth: '2px',
                    borderColor: '#d1d5db',
                    '&:hover': { borderColor: '#f59e0b' },
                    '&:focus-within': { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.2)' }
                  })
                }}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex flex-wrap items-center gap-4 justify-center">
            <div className="flex items-center">
              <div className="w-4 h-4 rounded bg-blue-500 mr-2"></div>
              <span className="text-xs font-semibold text-gray-700">New</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded bg-amber-500 mr-2"></div>
              <span className="text-xs font-semibold text-gray-700">In Progress</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded bg-green-500 mr-2"></div>
              <span className="text-xs font-semibold text-gray-700">Completed</span>
            </div>
            <div className="flex items-center ml-auto">
              <span className="text-xs font-bold text-gray-700">Total Tasks: {filteredTasks.length}</span>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <Calendar
            localizer={localizer}
            events={filteredTasks}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            titleAccessor="taskTitle"
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day', 'agenda']}
            defaultView="month"
            popup
            tooltipAccessor={(event) => `${event.taskTitle} - ${event.assignedToName}`}
          />
        </div>
      </div>
    </div>
  );
};

export default TaskCalendar;
